import React, { useContext, useEffect, useState } from "react"
import { ScrollView, View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

import AvatarM from "@common/components/avatar/AvatarM"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import UncontrolledTextInputM from "@common/components/input/UncontrolledTextInputM"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { liveService } from "@data/services/liveService"
import { useApiStatus } from "@common/hooks/useApiStatus"

const SCORE_COLOR = (score) => {
    if (score >= 60) return theme.colors.success
    if (score >= 40) return theme.colors.warning
    return theme.colors.error
}

const ENGAGEMENT_AXES = {
    assiduity: { color: theme.colors.success, name: "Présence", max: 40 },
    interaction: { color: theme.colors.phone, name: "Participation", max: 30 },
    productivity: { color: theme.colors.purple, name: "Productivité", max: 30 },
}


export const hasCharterBreach = (alerts) =>
    alerts?.some((a) => a.charter_breach) ?? false

export const ALERT_LABELS = {
    camera: "Caméra",
    lateness: "Retard",
    voice_interaction: "Peu d'échanges",
    productivity_score: "Productivité basse",
    activities_submitted: "Aucune activité",
    max_silent: "Silence prolongé",
    hand_raise: "Main levée ignorée",
    out_of_app: "Sortie de l'app",
    reconnections: "Reconnexions",
    bad_quality: "Connexion instable",
    device: "Téléphone",
    objective_mismatch: "Objectif incohérent",
}

// --- Sub-components ---

export const AlertBadge = ({ icon, label, color, bgColor }) => (
    <View
        style={{
            flexDirection: "row",
            alignItems: "center",
            backgroundColor: bgColor,
            borderRadius: 6,
            paddingHorizontal: 6,
            paddingVertical: 2,
        }}
    >
        <FontAwesome5 name={icon} size={9} color={color} />
        <TextM
            fontWeight="Bold"
            fontSize={10}
            color={color}
            style={{ marginLeft: 3 }}
        >
            {label}
        </TextM>
    </View>
)

export const AlertRow = ({ alert, indent = 24 }) => {
    const isBreach = alert.charter_breach
    const recurrenceCount = alert.recurrence_count

    return (
        <View style={{ marginTop: 8, marginLeft: indent }}>
            <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                {isBreach ? (
                    <AlertBadge
                        icon="file-contract"
                        label={alert.charter_rule ?? "Charte"}
                        color={theme.colors.error}
                        bgColor={theme.colors.errorLight}
                    />
                ) : (
                    <AlertBadge
                        icon="exclamation-triangle"
                        label={ALERT_LABELS[alert.indicator] ?? "Attention"}
                        color={theme.colors.warning}
                        bgColor={theme.colors.warningLight}
                    />
                )}
                {recurrenceCount >= 2 && (
                    <AlertBadge
                        icon="redo"
                        label={`${recurrenceCount}ème session consécutive`}
                        color={theme.colors.error}
                        bgColor={theme.colors.errorLight}
                    />
                )}
            </View>
            <TextM
                fontSize={13}
                color={isBreach ? theme.colors.errorDark : theme.colors.grey}
                wrap
                style={{ marginTop: 4 }}
            >
                {alert.message}
            </TextM>
        </View>
    )
}

export const ProxyCard = ({ proxy }) => {
    const isOk = proxy.status === "ok"
    const isBreach = hasCharterBreach(proxy.alerts)
    const color = isBreach
        ? theme.colors.error
        : isOk
          ? theme.colors.success
          : theme.colors.warning

    return (
        <View
            style={{
                backgroundColor: theme.colors.white,
                borderRadius: 12,
                padding: 14,
                marginBottom: 10,
                borderLeftWidth: 4,
                borderLeftColor: color,
                shadowColor: "#000",
                shadowOpacity: 0.05,
                shadowRadius: 4,
                shadowOffset: { width: 0, height: 2 },
                elevation: 2,
            }}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                }}
            >
                <FontAwesome5
                    name={
                        isOk
                            ? "check-circle"
                            : isBreach
                              ? "exclamation-circle"
                              : "exclamation-triangle"
                    }
                    size={16}
                    color={color}
                    solid
                />
                <TextM
                    fontWeight="SemiBold"
                    fontSize={15}
                    color={theme.colors.dark}
                    style={{ marginLeft: 8, flex: 1 }}
                >
                    {proxy.name}
                </TextM>
                <TextM fontWeight="Bold" fontSize={13} color={color}>
                    {isOk ? "OK" : isBreach ? "Alerte" : "Attention"}
                </TextM>
            </View>
            {!isOk &&
                proxy.alerts?.map((alert, i) => (
                    <AlertRow key={i} alert={alert} />
                ))}
        </View>
    )
}

export const StudentAxisRow = ({ axis }) => {
    const isOk = axis.status === "ok"
    const isBreach = hasCharterBreach(axis.alerts)
    const color = isBreach
        ? theme.colors.error
        : isOk
          ? theme.colors.success
          : theme.colors.warning

    return (
        <View style={{ marginTop: 8 }}>
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                }}
            >
                <FontAwesome5
                    name={
                        isOk
                            ? "check-circle"
                            : isBreach
                              ? "exclamation-circle"
                              : "exclamation-triangle"
                    }
                    size={13}
                    color={color}
                    solid
                />
                <TextM
                    fontSize={13}
                    fontWeight="SemiBold"
                    color={theme.colors.dark}
                    style={{ marginLeft: 6, flex: 1 }}
                >
                    {axis.name}
                </TextM>
                {!isOk && (
                    <TextM fontWeight="Bold" fontSize={11} color={color}>
                        {isBreach ? "Alerte" : "Attention"}
                    </TextM>
                )}
            </View>
            {!isOk &&
                axis.alerts?.map((alert, i) => (
                    <AlertRow key={i} alert={alert} indent={19} />
                ))}
        </View>
    )
}

export const EngagementScoreBar = ({ scores, totalScore }) => {
    if (!scores) return null
    const scoreColor = SCORE_COLOR(totalScore)

    return (
        <View style={{ marginBottom: 10 }}>
            {/* Total score above bar */}
            <View style={{ flexDirection: "row", justifyContent: "flex-end", marginBottom: 4 }}>
                <TextM fontWeight="Bold" fontSize={13} color={scoreColor}>
                    {totalScore}/100
                </TextM>
            </View>
            {/* Stacked bar */}
            <View
                style={{
                    flexDirection: "row",
                    height: 8,
                    backgroundColor: theme.colors.lighter,
                    borderRadius: 4,
                    overflow: "hidden",
                    marginBottom: 8,
                }}
            >
                {Object.keys(ENGAGEMENT_AXES).map((key) => {
                    const value = scores[key] ?? 0
                    const widthPct = (value / 100) * 100
                    return (
                        <View
                            key={key}
                            style={{
                                width: `${widthPct}%`,
                                height: "100%",
                                backgroundColor: ENGAGEMENT_AXES[key].color,
                            }}
                        />
                    )
                })}
            </View>
            {/* Labels */}
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
                {Object.keys(ENGAGEMENT_AXES).map((key) => {
                    const axis = ENGAGEMENT_AXES[key]
                    const value = scores[key] ?? 0
                    return (
                        <View key={key} style={{ flexDirection: "row", alignItems: "center" }}>
                            <View
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: axis.color,
                                    marginRight: 4,
                                }}
                            />
                            <TextM fontSize={11} color={theme.colors.grey}>
                                {axis.name}
                            </TextM>
                            <TextM
                                fontWeight="Bold"
                                fontSize={11}
                                color={axis.color}
                                style={{ marginLeft: 3 }}
                            >
                                {value}
                            </TextM>
                            <TextM fontSize={10} color={theme.colors.light}>
                                /{axis.max}
                            </TextM>
                        </View>
                    )
                })}
            </View>
        </View>
    )
}

const MissionPrescriptionBadge = ({ prescription }) => {
    if (prescription) {
        const activitiesCount =
            prescription.activities_count ??
            prescription.activity_ids?.length ??
            0
        return (
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor: theme.colors.successLight,
                    borderRadius: 8,
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    marginTop: 6,
                    alignSelf: "flex-start",
                }}
            >
                <FontAwesome5
                    name="robot"
                    size={10}
                    color={theme.colors.success}
                    style={{ marginRight: 4 }}
                    solid
                />
                <TextM
                    fontWeight="Bold"
                    fontSize={11}
                    color={theme.colors.success}
                >
                    Mission Sam · {activitiesCount} activité
                    {activitiesCount > 1 ? "s" : ""}
                </TextM>
            </View>
        )
    }
    return (
        <View
            style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: theme.colors.warningLight,
                borderRadius: 8,
                paddingHorizontal: 8,
                paddingVertical: 4,
                marginTop: 6,
                alignSelf: "flex-start",
            }}
        >
            <FontAwesome5
                name="exclamation-triangle"
                size={10}
                color={theme.colors.warning}
                style={{ marginRight: 4 }}
            />
            <TextM
                fontWeight="Bold"
                fontSize={11}
                color={theme.colors.warning}
            >
                Mission Sam non envoyée
            </TextM>
        </View>
    )
}

// Surfaces the β × Adoption_eleve component of the dual excellence
// score: how many activities the student finished between the previous
// live they attended and this one. Tutor sees the payoff of past
// prescriptions — green when the student worked off-live, orange when
// they did nothing, neutral when there's no prior live to reference yet.
const OffLiveAdoptionBadge = ({ count, hasPreviousLive }) => {
    if (count === undefined || count === null) {
        return null
    }

    let bgColor, fgColor, icon, label, solid
    if (!hasPreviousLive) {
        // First live with this student: there's no off-live window to score
        // against. Show a neutral signal so the tutor knows it isn't a
        // "0 activities" failure — the data simply doesn't apply yet.
        bgColor = theme.colors.lighter
        fgColor = theme.colors.grey
        icon = "info-circle"
        label = "Premier live avec cet élève — pas d'historique hors live"
        solid = false
    } else if (count > 0) {
        bgColor = theme.colors.successLight
        fgColor = theme.colors.success
        icon = "chart-line"
        label = `${count} activité${count > 1 ? "s" : ""} hors live faite${count > 1 ? "s" : ""} depuis le dernier live`
        solid = true
    } else {
        bgColor = theme.colors.warningLight
        fgColor = theme.colors.warning
        icon = "exclamation-triangle"
        label = "Aucune activité hors live depuis le dernier live"
        solid = false
    }

    return (
        <View
            style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: bgColor,
                borderRadius: 8,
                paddingHorizontal: 8,
                paddingVertical: 4,
                marginTop: 6,
                alignSelf: "flex-start",
            }}
        >
            <FontAwesome5
                name={icon}
                size={10}
                color={fgColor}
                style={{ marginRight: 4 }}
                solid={solid}
            />
            <TextM fontWeight="Bold" fontSize={11} color={fgColor}>
                {label}
            </TextM>
        </View>
    )
}

const StudentCard = ({ student, index, missionPrescription, hasMissionFlow }) => {
    const breachCount = student.axes?.reduce(
        (n, axis) => n + (hasCharterBreach(axis.alerts) ? 1 : 0),
        0
    ) ?? 0
    const isMissionBreach = hasMissionFlow && !missionPrescription
    const showLeftBorder = breachCount > 0 || isMissionBreach
    const leftBorderColor =
        breachCount > 0 ? theme.colors.error : theme.colors.warning

    return (
        <View
            style={{
                backgroundColor: theme.colors.white,
                borderRadius: 12,
                padding: 14,
                marginBottom: 10,
                borderLeftWidth: showLeftBorder ? 4 : 0,
                borderLeftColor: leftBorderColor,
                shadowColor: "#000",
                shadowOpacity: 0.05,
                shadowRadius: 4,
                shadowOffset: { width: 0, height: 2 },
                elevation: 2,
            }}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 8,
                }}
            >
                <AvatarM
                    size={36}
                    user={{
                        id: student.student_id,
                        display_name: student.display_name,
                        avatar: student.avatar,
                    }}
                    style={{ marginRight: 10 }}
                />
                <View style={{ flex: 1 }}>
                    <TextM
                        fontWeight="Bold"
                        fontSize={15}
                        color={theme.colors.dark}
                    >
                        {student.display_name}
                    </TextM>
                    {hasMissionFlow && (
                        <MissionPrescriptionBadge
                            prescription={missionPrescription}
                        />
                    )}
                    <OffLiveAdoptionBadge
                        count={student.activities_completed_since_last_live}
                        hasPreviousLive={student.has_previous_live_with_student}
                    />
                </View>
            </View>
            {student.total_score != null && (
                <EngagementScoreBar
                    scores={student.scores}
                    totalScore={student.total_score}
                />
            )}
            {student.axes?.map((axis, i) => (
                <StudentAxisRow key={i} axis={axis} />
            ))}
        </View>
    )
}

const SummarySection = ({ summary }) => {
    const variantConfig = {
        success: {
            icon: "check-circle",
            bgColor: theme.colors.successLight,
            color: theme.colors.success,
        },
        neutral: {
            icon: "info-circle",
            bgColor: theme.colors.warningLight,
            color: theme.colors.warning,
        },
        attentive: {
            icon: "eye",
            bgColor: theme.colors.warningLight,
            color: theme.colors.warning,
        },
    }

    const config = variantConfig[summary?.variant] ?? variantConfig.neutral

    return (
        <View
            style={{
                backgroundColor: config.bgColor,
                borderRadius: 12,
                padding: 16,
                flexDirection: "row",
                alignItems: "center",
                marginBottom: 6,
            }}
        >
            <FontAwesome5
                name={config.icon}
                size={24}
                color={config.color}
                solid
            />
            <TextM
                fontWeight="SemiBold"
                fontSize={14}
                color={theme.colors.dark}
                wrap
                style={{ marginLeft: 12, flex: 1 }}
            >
                {summary?.message}
            </TextM>
        </View>
    )
}

const SectionHeader = ({ icon, title }) => (
    <View
        style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: 18,
            marginBottom: 10,
        }}
    >
        <FontAwesome5
            name={icon}
            size={14}
            color={theme.colors.primary}
            solid
        />
        <TextM
            fontWeight="Bold"
            fontSize={16}
            color={theme.colors.dark}
            style={{ marginLeft: 8 }}
        >
            {title}
        </TextM>
    </View>
)

// --- Main Screen ---

const QualityClosureScreen = (props) => {
    const { navigation, route } = props
    const occurrenceId = route?.params?.occurrence_id
    const liveSessionId = route?.params?.liveSessionId
    const studyMissionPrescriptions = route?.params?.studyMissionPrescriptions
    const hasMissionFlow = studyMissionPrescriptions !== undefined
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()

    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [comment, setComment] = useState("")
    const [savingComment, setSavingComment] = useState(false)

    const fetchData = () => {
        setLoading(true)
        setError(false)
        props.apiIdle(liveService.getQualityClosure)
        props.apiRequest(liveService.getQualityClosure, {
            id: occurrenceId,
        })
    }

    useEffect(() => {
        if (occurrenceId) {
            fetchData()
        }
    }, [occurrenceId])

    useApiStatus(
        liveService.getQualityClosure,
        null,
        false,
        (successData) => {
            setData(successData)
            setComment(successData?.quality_closure_comment ?? "")
            setLoading(false)
        },
        () => {
            setError(true)
            setLoading(false)
        }
    )

    useApiStatus(
        liveService.saveQualityClosureComment,
        null,
        false,
        () => {
            setSavingComment(false)
        },
        () => {
            setSavingComment(false)
        }
    )

    const proceedToEnd = () => {
        // Save comment if provided
        if (comment.trim()) {
            props.apiRequest(liveService.saveQualityClosureComment, {
                id: occurrenceId,
            }, { comment: comment.trim() })
        }

        navigation.replace("end-session-tutor", {
            occurrence_id: occurrenceId,
            liveSessionId,
        })
    }

    const handleContinue = () => {
        if (hasMissionFlow && data?.students?.length) {
            const missingNames = data.students
                .filter((s) => !studyMissionPrescriptions[s.student_id])
                .map((s) => s.display_name)

            if (missingNames.length > 0) {
                DialogM.show({
                    text1:
                        missingNames.length > 1
                            ? "Missions Sam non envoyées"
                            : "Mission Sam non envoyée",
                    text2:
                        `Aucune mission envoyée à : ${missingNames.join(", ")}.\n\n` +
                        "Tu peux revenir l'envoyer ou clôturer le live malgré l'alerte.",
                    buttonText1: "Revenir envoyer",
                    onPress: () => navigation.goBack(),
                    buttonText3: "Clôturer quand même",
                    buttonColor3: theme.colors.warning,
                    onPress3: () => proceedToEnd(),
                    exitButton: true,
                    onPressBack: () => {},
                })
                return
            }
        }

        proceedToEnd()
    }

    const containerWidth =
        dimensions?.width > 600
            ? 560
            : dimensions?.width
              ? dimensions.width - 32
              : "100%"

    // Loading state
    if (loading) {
        return (
            <View
                style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: theme.colors.greyBg,
                }}
            >
                <ActivityIndicator
                    animating
                    color={theme.colors.primary}
                    size="large"
                />
            </View>
        )
    }

    // Error state
    if (error) {
        return (
            <View
                style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: theme.colors.greyBg,
                    paddingHorizontal: 20,
                }}
            >
                <TextM
                    fontWeight="SemiBold"
                    fontSize={16}
                    color={theme.colors.dark}
                    center
                >
                    Une erreur est survenue
                </TextM>
                <View
                    style={{
                        flexDirection: "row",
                        marginTop: 20,
                        gap: 12,
                    }}
                >
                    <FilledButtonM
                        label="Réessayer"
                        onPress={fetchData}
                        style={{ width: null, paddingHorizontal: 20 }}
                    />
                    <FilledButtonM
                        label="Passer"
                        color={theme.colors.light}
                        onPress={() => {
                            navigation.replace("end-session-tutor", {
                                occurrence_id: occurrenceId,
                                liveSessionId,
                            })
                        }}
                        style={{ width: null, paddingHorizontal: 20 }}
                    />
                </View>
            </View>
        )
    }

    const hasStudents = (data?.connected_students_count ?? 0) > 0

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.colors.greyBg,
            }}
        >
            <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{
                    alignItems: "center",
                    paddingBottom: 20,
                }}
            >
                <View
                    style={{
                        width: containerWidth,
                        paddingTop: 24,
                    }}
                >
                    {/* Header */}
                    <TextM
                        fontWeight="Bold"
                        fontSize={20}
                        color={theme.colors.primary}
                        center
                    >
                        Bilan qualité
                    </TextM>
                    <TextM
                        fontSize={13}
                        color={theme.colors.light}
                        center
                        style={{ marginTop: 4, marginBottom: 16 }}
                    >
                        {data?.connected_students_count ?? 0} élève(s) connecté(s)
                    </TextM>

                    {/* Summary */}
                    <SummarySection summary={data?.summary} />

                    {/* Section 1 — Tutor proxies */}
                    <SectionHeader icon="chalkboard-teacher" title="Les bases" />
                    {data?.tutor_proxies?.map((proxy, i) => (
                        <ProxyCard key={i} proxy={proxy} />
                    ))}

                    {/* Section 2 — Students */}
                    <SectionHeader icon="user-graduate" title="Tes élèves" />
                    {hasStudents ? (
                        data?.students?.map((student, i) => (
                            <StudentCard
                                key={i}
                                student={student}
                                index={i}
                                hasMissionFlow={hasMissionFlow}
                                missionPrescription={
                                    studyMissionPrescriptions?.[student.student_id]
                                }
                            />
                        ))
                    ) : (
                        <TextM
                            fontSize={14}
                            color={theme.colors.light}
                            center
                            style={{ marginVertical: 10 }}
                        >
                            Aucun élève ne s'est connecté à cette session.
                        </TextM>
                    )}

                    {/* Section 3 — Feedback */}
                    <SectionHeader icon="comment-alt" title="Aide-nous à améliorer ce bilan" />
                    <TextM
                        fontSize={13}
                        color={theme.colors.light}
                        wrap
                        style={{ marginBottom: 8 }}
                    >
                        Tu vois une erreur ou une info manquante ?
                    </TextM>
                    <UncontrolledTextInputM
                        placeholder="Ex : un indicateur semble incorrect, un élève manque..."
                        multiline
                        numberOfLines={3}
                        value={comment}
                        onChangeText={setComment}
                        style={{
                            minHeight: 80,
                            backgroundColor: theme.colors.white,
                        }}
                    />
                </View>
            </ScrollView>

            {/* Bottom button */}
            <View
                style={{
                    backgroundColor: theme.colors.white,
                    paddingTop: 10,
                    paddingBottom: Math.max(insets.bottom, 10),
                    paddingHorizontal: 16,
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.lightMore,
                    alignItems: "center",
                }}
            >
                <FilledButtonM
                    label="Continuer"
                    onPress={handleContinue}
                    loading={savingComment}
                    style={{ width: containerWidth }}
                />
            </View>
        </View>
    )
}

const mapStateToProps = (state) => ({
    user: state.data.currentUser?.object,
})

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(QualityClosureScreen)
